import { debounceTime } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { SapiEntity } from './../../../services/sapi.service';
import { BbChannelComponent } from '../../shared/bb-channel/bb-channel.component';
import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { Location } from '@angular/common';
import { NgForm } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { AbstractDetailsComponent } from '../../shared/abstract-details.component';
import { Observable, Subject } from 'rxjs';
import { UUIDV4 } from '../../../shared/tools/UUIDV4';
import { Channel, ChannelBlock, ChannelBlockPlayerLayout, ChannelBlockType, ChannelBlockPromoLayout, BasicList } from '../../../interfaces/channel';
import { bbDeepCopy } from 'app/shared/utils/bbDeepCopy.util';
import { ChannelDetailsElementsComponent } from './channel-details-elements.component';
import { WebSocketsService } from 'app/services/web-sockets.service';
import { RadioListImage } from 'app/components/shared/bb-radio-list/bb-radio-list.component';

@Component({
	selector: 'channel-details',
	templateUrl: './channel-details.component.html',
	styleUrls: ['./channel-details.component.scss']
})

export class ChannelDetailsComponent extends AbstractDetailsComponent implements OnInit, AfterViewInit {

	@ViewChild('formRef', { static: true }) form: NgForm;
	@ViewChild('channelPreview') channelPreview: BbChannelComponent;
	@ViewChild(ChannelDetailsElementsComponent) channelDetailsElementsComponent: ChannelDetailsElementsComponent;

	public playerBlocks: ChannelBlock<ChannelBlockType>[] = [];
	public previewWidth: '100%' | '800px' | '478px' = '100%';
	public previewOptions: RadioListImage[];
	backgroundColorPreset: BasicList[];

	private _channel: Channel;

	get channel(): Channel {
		return this._channel;
	}

	constructor(
		protected route: ActivatedRoute,
		protected location: Location,
		protected translate: TranslateService,
		private webSocketsService: WebSocketsService
	) {

		super(route, location);
		this.entityConfiguration = {
			name: 'channel',
			entityType: 'Channel',
			parentPath: '/library/channels',
			basePath: '/library/channel/',
			versioned: true,
			showVersionMetadata: true,
			versionMetadataToSave: {
				bbcomment: ''
			},
			extraEntityParams: {
				includeContent: 'false'
			},
			defaults: {
				subtype: 'versioned',
				playIn: 'detailPage',
				borderRadius: 5,
				pagePadding: 8,
				maxWidth: 0,
				blocks: [],
				colorTheme: 'light',
				backgroundColor: 'ffffff', // white
				fontFamilyPrimary: 'Montserrat',
				fontFamilySecondary: 'Montserrat',
				primaryFontColor: '141414',
				secondaryFontColor: '141414',
				detailPageConfig: {
					withinBorder: false,
					playerAlignment: 'left',
					backgroundColor: 'ededed',
					showThumbnailAsBackground: true,
					enableBackgroundBlur: true,
					showRelatedItems: true,
				}
			}
		};
	}

	ngOnInit() {
		super.ngOnInit();
		if (this.isNew) {
			this._channel = this.entity as Channel;
		}

		this.previewOptions = [
			{ label: this.translate.instant('DESKTOP'), value: '100%', icon: 'display' },
			{ label: this.translate.instant('TABLET'), value: '800px', icon: 'tablet', iconStyle: 'regular' },
			{ label: this.translate.instant('MOBILE'), value: '478px', icon: 'mobile', iconStyle: 'regular' }
		];

		this.backgroundColorPreset = [
			{ label: this.translate.instant('GREY_DARK'), value: '1e1e1e' },
			{ label: this.translate.instant('GREY_LIGHT'), value: 'ededed' },
			{ label: this.translate.instant('WHITE'), value: 'ffffff' }, // default
		];
	}

	ngAfterViewInit() {
		// this.subscriptions from other class, will be cleaned up in parent class as well
		this.subscriptions
			.add(this.formGroup.valueChanges.pipe(debounceTime(1000))
				.subscribe((valueChanges) => {
					this.onFormValueChanges(valueChanges);
				}));
	}

	cancel(): Subject<SapiEntity> {
		const cancelSubject = super.cancel();
		cancelSubject.subscribe(() => {
			this.onFormValueChanges(null); // not updated through form, manually trigger for img reset
		});
		return cancelSubject;
	}

	onLogoChanged(id: string) {
		this.onFormValueChanges(null); // clear img or already uploaded

		// new img? update again after loaded
		this.subscriptions.add(this.webSocketsService.subscribeToChannel('MediaClip').subscribe((websocketEvent: any) => {
			if (websocketEvent.payload && websocketEvent.payload.id === id) {
				if (websocketEvent.ev === 'ingest' && websocketEvent.status === 'success') {
					// set cache value so channels can check if it needs to reload broken imgs
					this.channelPreview.updateChannel({ ...this._channel, ...{ imgCacheDate: new Date().getTime() } });
				}
			}
		}));
	}

	onFormValueChanges(formValues: any) {
		if (this.channelPreview) {
			this.channelPreview.updateChannel(this._channel);
		}
		this.entity = this.channel;
	}

	deleteBlock(id: string) {
		if (this._channel.blocks) {
			let result = this._channel.blocks.findIndex((block: ChannelBlock<ChannelBlockType>) => block.id === id);
			if (result !== -1) {
				// look for references and notify the user
				if (this._channel.blocks[result].type === 'Player') {
					this.channel.blocks.forEach(block => {

						if ('playIn' in block.config && block.config.playIn === 'player') {
							this.overlayService.createToast('Blocks that used this player have been reset.', 'warning');
							block.config.playIn = null;
						}
					});

					if (this._channel.playIn === 'player') {
						this.overlayService.createToast('Channel default playing has been reset.', 'warning');
						this._channel.playIn = 'detailPage';
					}
				}
				this._channel.blocks.splice(result, 1);
				this.entity = this.channel;
				this.form.form.markAsDirty();
			}

			result = this.playerBlocks.findIndex((block: ChannelBlock<ChannelBlockType>) => block.id === id);
			if (result !== -1) {
				this.fillPlayerBlocks();
			}
		}
	}

	copyBlock(id: string) {
		if (this._channel.blocks) {
			const index = this._channel.blocks.findIndex((block: ChannelBlock<ChannelBlockType>) => block.id === id);
			if (index !== -1) {
				if (this._channel.blocks[index].type === 'Player') {
					return;
				}

				const copy = bbDeepCopy(this._channel.blocks[index]);
				copy.id = 'cb' + UUIDV4.getUniqueId();

				this._channel.blocks.splice(index + 1, 0, copy);
				this.openNewElement(index + 1);
				this.entity = this.channel;
				this.form.form.markAsDirty();
			}
		}
	}

	addBlock(input: { type: ChannelBlockType; subType?: ChannelBlockPlayerLayout; promoType?: ChannelBlockPromoLayout }) {
		const type = input.type;
		const subType = input.subType;
		const promoType = input.promoType;

		const block: Partial<ChannelBlock<typeof type>> = {};
		block.type = type;
		block.id = 'cb' + UUIDV4.getUniqueId();

		switch (type) {
			case 'HeaderLogoTitle':
				(block as ChannelBlock<typeof type>).config = {
					logoId: '',
					title: '',
				};
				break;
			case 'Player':
				(block as ChannelBlock<typeof type>).config = {
					contentType: 'cliplist',
					contentId: null,
					layout: subType || 'single',
					showPlayerTitle: true,
					showDescription: true,
					showClipTitles: true,
					playlistLayout: 'Cliplist'
				};
				break;
			case 'Grid':
				(block as ChannelBlock<typeof type>).config = {
					header: '',
					contentId: null,
					playIn: null,
					showClipTitles: true,
					aspectRatio: null,
				};
				break;
			case 'Cliplist':
				(block as ChannelBlock<typeof type>).config = {
					header: '',
					contentId: null,
					playIn: null,
					showClipTitles: true,
					aspectRatio: null,
				};
				break;
			case 'Featured':
				(block as ChannelBlock<typeof type>).config = {
					header: '',
					contentType: 'mediaclip',
					contentId: null,
					featuredImageId: '',
					playIn: null
				};
				break;
			case 'Promotional':
				(block as ChannelBlock<typeof type>).config = {
					contentId: null,
					promoLayout: promoType || 'large' ,
					promotionalUrl: '',
					promotionalImageId: '',
					openInNewTab: true
				};
				break;
			case 'FeaturedHeader':
				(block as ChannelBlock<typeof type>).slides = [{
					id: UUIDV4.getUniqueId(),
					header: '',
					description: '',
					contentType: 'mediaclip',
					contentId: null,
					featuredImageId: null,
					logoImageId: null
				}];
				(block as ChannelBlock<typeof type>).config = {
					playIn: null,
					logoImageId: null,
					imageFitMode: 'fit'
				};
				break;
			case 'Carousel':
				(block as ChannelBlock<typeof type>).config = {
					header: '',
					contentId: null,
					playIn: null,
					showClipTitles: true,
					aspectRatio: null,
				};
				break;
		}

		if (this._channel.blocks) {
			let openIndex = 0;
			if (['HeaderLogoTitle', 'FeaturedHeader'].includes(type)) {
				this._channel.blocks.unshift(block as ChannelBlock<typeof type>);
			} else {
				this._channel.blocks.push(block as ChannelBlock<typeof type>);
				openIndex = this._channel.blocks.length - 1;
			}
			this.openNewElement(openIndex);
			this.entity = this.channel;
			this.form.form.markAsDirty();
			this.fillPlayerBlocks();
		}
	}

	openNewElement(index: number) {
		setTimeout(() => {
			this.channelDetailsElementsComponent.collapseElement(index, true);
		});
	}

	public setPreviewWidth(): void {
		// trigger resize for when player didn't scale correctly yet between width changes
		setTimeout(() => {
			window.dispatchEvent(new Event('resize'));
		}, 100);
	}

	// converting older versions. will be obsolete in a while (june 18 2020)
	load(id: string): Observable<SapiEntity> {
		const loadObservable = super.load(id);
		loadObservable.subscribe((entity: Channel) => {
			this._channel = entity;
			this.fillPlayerBlocks();
		});

		return loadObservable;
	}

	save(force: boolean): Observable<any> {
		const saveObservable = super.save(force, null, false);
		saveObservable.subscribe((entity: Channel) => {
			this._channel = entity;
		});

		return saveObservable;
	}

	fillPlayerBlocks() {
		this.playerBlocks = this.channel.blocks.filter(block => block.type === 'Player');
	}
}
